import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Boxes, Users, Truck, UserCog, Laptop, LogOut } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const staffNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/inventory", label: "Inventory", icon: Boxes, testid: "nav-inventory" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/rentals", label: "Deliveries & Returns", icon: Truck, testid: "nav-rentals" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const nav = [...staffNav];
  if (user?.role === "admin") {
    nav.push({ to: "/team", label: "Team", icon: UserCog, testid: "nav-team" });
  }
  const isClient = user?.role === "client";

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-[#0A2540] text-slate-200 fixed inset-y-0 left-0">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/10">
          <div className="h-9 w-9 rounded-md bg-white/10 flex items-center justify-center">
            <Laptop className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-heading font-extrabold text-white leading-none">PC Panda</p>
            <p className="text-[10px] tracking-widest text-slate-400">TECHNOLOGIES</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {(isClient ? [{ to: "/my-rentals", label: "My Rentals", icon: Truck, testid: "nav-my-rentals" }] : nav).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                  isActive ? "bg-white text-[#0A2540]" : "text-slate-300 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <item.icon className="h-4.5 w-4.5" style={{ height: 18, width: 18 }} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-white/15 text-white text-xs">
                {user?.name?.slice(0, 2)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
              <p className="text-[11px] text-slate-400 capitalize">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-white/10 hover:text-white transition-colors duration-150"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden h-14 bg-[#0A2540] text-white flex items-center justify-between px-4 sticky top-0 z-20">
          <div className="flex items-center gap-2">
            <Laptop className="h-5 w-5" />
            <span className="font-heading font-bold">PC Panda</span>
          </div>
          <button onClick={logout} data-testid="logout-button-mobile" className="text-slate-200">
            <LogOut className="h-5 w-5" />
          </button>
        </header>
        <main className="flex-1 w-full">
          <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 py-6 lg:py-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
